import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';
import { CryptoService } from './crypto.service';
@Injectable()
export class SessionsService extends BaseHttpClientService {
  private cryptoService: CryptoService = new CryptoService();

  constructor(private http: HttpClient) {
    super()
  }

  public getAllSiteSessions(pagination, sortproperty, sortdirection, from?, to?, site?, company?) {
    const dateString = '?startDate=' + from + '&endDate=' + to;
    // The id breaks ties in the sort, without which pages can repeat or skip sessions that started at the same time.
    const sortingString = '&sort=' + sortproperty + ',' + sortdirection + '&sort=id,asc';
    const queryString = '&size=' + pagination.size + '&page=' + pagination.page;
    const siteOnlyString = '&siteId=' + site;
    const companyOnlyString = '&maintenanceId=' + company;
    const siteAndCompanyString = siteOnlyString + companyOnlyString;
    let paramString;

    if (site !== undefined && company !== undefined) {
      paramString = dateString + sortingString + queryString + siteAndCompanyString;
    } else if (site !== undefined && company === undefined) {
      paramString = dateString + sortingString + queryString + siteOnlyString;
    } else if (site === undefined && company !== undefined) {
      paramString = dateString + sortingString + queryString + companyOnlyString;
    } else {
      paramString = dateString + sortingString + queryString;
    }

    return this.http.get(environment.apiUrl + 'api/ext-site-session/for-dates-and-ids/list' + paramString, { observe: 'response' })
      .pipe(map((res: HttpResponse<any>) => this.handleDataWithHeaders(res)))
      .pipe(catchError(this.handleError));
  }


  public getAllWorkerSessions(
    pagination,
    sort,
    from?,
    to?,
    site?,
    company?,
    worker?,
    sessionType?,
    useLatestOnlyHeader: boolean = false,
    includeComments: boolean = false
  ) {

    let params = new HttpParams();
    params = params.append('startDate', from);
    params = params.append('endDate', to);
    params = params.append('size', pagination.size);
    params = params.append('page', pagination.page);
    params = params.append('sort', sort);
    // The id breaks ties in the sort, without which pages can repeat or skip sessions that started at the same time.
    params = params.append('sort', 'id,asc');
    if(company){
      params = params.append('maintenanceId', company);
    }
    if(site){
      params = params.append('siteId', site);
    }
    if(worker){
      params = params.append('workerUserId', worker);
    }
    if(sessionType){
      params = params.append('sessionType', sessionType);
    }
    if (includeComments) {
      params = params.append('includeComments', 'true');
    }

    const requestOptions: { observe: 'response'; params: HttpParams; headers?: HttpHeaders; } = {
      observe: 'response',
      params
    };

    if (useLatestOnlyHeader) {
      requestOptions.headers = this.applyLatestOnlyHeader();
    }

    return this.http.get(environment.apiUrl + 'api/ext-user-session/for-dates-and-ids/list', requestOptions)
      .pipe(map((res: HttpResponse<any>) => this.handleDataWithHeaders(res)))
      .pipe(catchError(this.handleError));
  }

  public getUserSessionById(id: number): Observable<any> {
    return this.http.get(environment.apiUrl + 'api/ext-user-session/' + id)
      .pipe(catchError(this.handleError));
  }

  //get the last user session where worker is session owner
  public getLatestOwnerUserSessionForWorkOrder(workOrderIdId: number): Observable<any> {

    return (this.http.get(environment.apiUrl + `api/ext-user-session/last-lead-session-for-work-order/${workOrderIdId}`, { observe: 'response' }))
      .pipe(map((res: HttpResponse<any>) => this.handleDataWithHeaders(res)))
      .pipe(catchError(this.handleError));
  }

  // Get site session by work order id
  public getSiteSessionByWorkOrderId(workorderid): Observable<any> {
    return this.http.get(environment.apiUrl + 'api/ext-site-session/by-work-order/' + workorderid)
      .pipe(catchError(error => {
        if (error.status === 404) {
          // ok, just doesn't have a session
          return Promise.resolve();
        }
        // any other error
        return this.handleError(error);
      }));
  }

  // Get worker sessions by site session id
  public getUserSessionsBySiteSessionId(siteSessionId): Observable<any> {
    return this.http.get(environment.apiUrl + 'api/ext-user-session/by-site-session/' + siteSessionId + '?size=10000000&page=0')
      .pipe(catchError(this.handleError));
  }

  // CALL CENTER CHECK OUT USER
  public checkoutWorker(obj) {

    return this.http.post(environment.apiUrl + 'api/ext-site-session/callcenter-check-out', obj)
      .pipe(catchError(this.handleError));
  }

  // CALL CENTER CHECK OUT MANAGER
  // Which is the same as closing a session

  public checkoutManagement(obj) {
    return this.http.post(environment.apiUrl + 'api/ext-site-session/callcenter-management-check-out', obj)
      .pipe(catchError(this.handleError));
  }

  /* Get the what 3 words using latitude and longitude */
  public getWhat3WordsLocation(lat: number, lng: number): Observable<any> {
    //add skip header - so that we dont add auth token to this request
    const skipHeader = new HttpHeaders().set(environment.InterceptorSkipHeader, '');
    const what3WordsApiKey = this.cryptoService.decryptText(localStorage.getItem(environment.keys.what3words));
    let params = new HttpParams();
    params = params.append('key', what3WordsApiKey);
    params = params.append('coordinates', `${lat},${lng}`);
    return this.http.get(environment.what3wordsUri, { headers: skipHeader, params: params })
      .pipe(catchError(this.handleError));
  }

  public sendNewNotes(body): Observable <any> {
    return this.http.post(environment.apiUrl + 'api/ext-user-session/set-note', body)
      .pipe(catchError(this.handleError));
  }

  public updateExpectedCheckOffTime(userSessionId, expectedCheckOffTime): Observable<any> {
    const body = {
      expectedCheckOffTime: expectedCheckOffTime
    };
    return this.http.put(environment.apiUrl + 'api/ext-user-session/' + userSessionId + '/set-expected-off-time', body)
    .pipe(catchError(this.handleError));
  }

  public handleDataWithHeaders(res: HttpResponse<any>) {
    const headers = res.headers;
    const body = res.body;
    const data = {
      'headers': headers,
      'body': body
    };
    return data;
  }
}
